/**
 * Stripe Client Configuration
 * 
 * This module configures Stripe for client-side operations including
 * Elements integration and payment processing.
 * 
 * Note: Client-side code should import directly from './client'
 * to avoid importing server environment variables in the browser
 */

import { loadStripe, Stripe } from '@stripe/stripe-js'

let stripeInstance: Stripe | null

/**
 * Get Stripe instance for client-side operations
 * @returns Promise<Stripe | null>
 */
export const getStripe = async () => {
  if (!stripeInstance) {
    const publishableKey = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
    
    if (!publishableKey) {
      console.error('Stripe publishable key not found in environment variables')
      return Promise.resolve(null)
    }
    
    stripeInstance = await loadStripe(publishableKey)
  }
  
  return stripeInstance
}

/**
 * Stripe Elements options for consistent styling
 */
export const stripeElementsOptions = {
  appearance: {
    theme: 'night' as const,
    variables: {
      colorPrimary: '#3b82f6', // Blue-500
      colorBackground: '#1f2937', // Gray-800
      colorText: '#f9fafb', // Gray-50
      colorDanger: '#ef4444', // Red-500
      fontFamily: 'system-ui, -apple-system, sans-serif',
      spacingUnit: '4px',
      borderRadius: '8px',
    },
    rules: {
      '.Input': {
        backgroundColor: '#374151', // Gray-700
        border: '1px solid #6b7280', // Gray-500
        color: '#f9fafb', // Gray-50
      },
      '.Input:focus': {
        border: '1px solid #3b82f6', // Blue-500
        boxShadow: '0 0 0 1px #3b82f6',
      },
      '.Label': {
        color: '#f9fafb',
        fontSize: '14px',
        fontWeight: '500',
      },
      '.Error': {
        color: '#ef4444',
        fontSize: '14px',
      }
    }
  }
}

/**
 * Stripe payment element options
 */
export const paymentElementOptions = {
  layout: {
    type: 'tabs' as const,
    defaultCollapsed: false,
  },
  paymentMethodOrder: ['card', 'klarna', 'afterpay_clearpay']
}